import React, { useState, useEffect } from 'react';
import './App.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp, faDiscord } from '@fortawesome/free-brands-svg-icons';
import { faLocationDot, faHouse, faUser, faGamepad, faClipboard, faMusic, faDiceD20, faMicrophone, faTrophy } from '@fortawesome/free-solid-svg-icons';
import Tournament from './components/Tournament';
import BotC from './components/BotC';
import DrinkingGames from './components/DrinkingGames';
import BotConfig from './components/BotConfig';
import Music from './components/Music';
import GameTracker from './components/GameTracker';
import Leaderboards from './components/Leaderboards';
import AIChat from './components/AIChat';
import * as discordApi from './services/discordApi';
import type { DiscordMember, VoiceChannel } from './services/discordApi';

interface Birthday {
  name: string;
  month: number;
  day: number;
}

function App() {
  const [currentPage, setCurrentPage] = useState<'home' | 'bot-config' | 'activities' | 'profiles' | 'board' | 'music' | 'dnds' | 'tournament' | 'botc' | 'drinking-games' | 'game-tracker' | 'leaderboards' | 'ai-chat'>('home');
  const [leftPanelOpen, setLeftPanelOpen] = useState(false);
  const [rightPanelOpen, setRightPanelOpen] = useState(false);

  // Discord data state
  const [onlineMembers, setOnlineMembers] = useState<DiscordMember[]>([]);
  const [voiceChannels, setVoiceChannels] = useState<VoiceChannel[]>([]);
  const [discordLoading, setDiscordLoading] = useState(true);
  const [discordError, setDiscordError] = useState<string | null>(null);

  const activities = [
    { id: 1, name: 'Drinking Games', color: 'bg-[#8bd3dd]' },
    { id: 2, name: 'Random Spinner', color: 'bg-[#9b5de5]' },
    { id: 3, name: 'BotC', color: 'bg-[#f582ae]' },
    { id: 4, name: 'Tournament', color: 'bg-[#ffd803]' },
    { id: 5, name: 'Hedbadnz', color: 'bg-[#b8f2e6]' },
  ];

  const birthdays: Birthday[] = [
    { name: 'Quetzali', month: 1, day: 10 },
    { name: 'Javier', month: 1, day: 16 },
    { name: 'Charlie', month: 2, day: 10 },
    { name: 'Eugenio', month: 3, day: 12 },
    { name: 'JJ', month: 3, day: 20 },
    { name: 'Limo', month: 3, day: 29 },
    { name: 'David', month: 4, day: 9 },
    { name: 'Chapa', month: 4, day: 20 },
    { name: 'Somi Joto', month: 4, day: 27 },
    { name: 'Lidia', month: 5, day: 19 },
    { name: 'Miranda', month: 5, day: 23 },
    { name: 'Bruno', month: 6, day: 28 },
    { name: 'Nina', month: 7, day: 1 },
    { name: 'Paola', month: 7, day: 19 },
    { name: 'Mau Guerra', month: 8, day: 8 },
    { name: 'Melicoff', month: 8, day: 31 },
    { name: 'Pato', month: 9, day: 24 },
    { name: 'Betty', month: 9, day: 27 },
    { name: 'Dante', month: 10, day: 3 },
    { name: 'Harper', month: 10, day: 16 },
    { name: 'Lynn', month: 10, day: 21 },
    { name: 'Mike', month: 11, day: 3 },
    { name: 'Alex M.', month: 12, day: 7 },
  ];

  const getUpcomingBirthdays = () => {
    const today = new Date();
    const currentYear = today.getFullYear();

    const birthdaysWithDates = birthdays.map(b => {
      let date = new Date(currentYear, b.month - 1, b.day);
      if (date < today) {
        date = new Date(currentYear + 1, b.month - 1, b.day);
      }
      const daysUntil = Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      return { ...b, date, daysUntil };
    });

    return birthdaysWithDates
      .sort((a, b) => a.daysUntil - b.daysUntil)
      .slice(0, 5);
  };

  const formatDate = (month: number, day: number) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[month - 1]} ${day}`;
  };

  const upcomingBirthdays = getUpcomingBirthdays();

  const navItems = [
    { id: 'home', label: 'Home', icon: faHouse },
    { id: 'profiles', label: 'Profiles', icon: faUser },
    { id: 'activities', label: 'Activities', icon: faGamepad },
    { id: 'game-tracker', label: 'Game Tracker', icon: faGamepad },
    { id: 'leaderboards', label: 'Leaderboards', icon: faTrophy },
    { id: 'ai-chat', label: 'Discord Msg', icon: faDiscord },
    { id: 'board', label: 'Board', icon: faClipboard },
    { id: 'music', label: 'Music', icon: faMusic },
    { id: 'dnds', label: 'DnDs', icon: faDiceD20 },
  ];

  // Fetch Discord data
  useEffect(() => {
    const fetchDiscordData = async () => {
      try {
        setDiscordLoading(true);
        setDiscordError(null);

        // Fetch online members and voice channels in parallel
        const [membersResponse, voiceResponse] = await Promise.all([
          discordApi.getOnlineMembers(),
          discordApi.getVoiceChannels(),
        ]);

        if (membersResponse.success && membersResponse.data) {
          setOnlineMembers(membersResponse.data);
        } else {
          throw new Error(membersResponse.error || 'Failed to fetch members');
        }

        if (voiceResponse.success && voiceResponse.data) {
          setVoiceChannels(voiceResponse.data);
        }

        setDiscordLoading(false);
      } catch (error) {
        console.error('Error fetching Discord data:', error);
        setDiscordError(error instanceof Error ? error.message : 'Connection failed');
        setDiscordLoading(false);
      }
    };

    // Initial fetch
    fetchDiscordData();

    // Refresh every 5 minutes
    const interval = setInterval(fetchDiscordData, 300000);

    return () => clearInterval(interval);
  }, []);

  // Left Navigation Panel Component
  const LeftPanel = () => (
    <div
      className={`fixed left-0 top-0 h-full w-64 bg-[#001858] border-r-4 border-black shadow-[8px_0px_0px_0px_rgba(0,0,0,1)] z-40 transition-transform duration-300 ease-in-out ${
        leftPanelOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0`}
      style={{ willChange: 'transform' }}
    >
      <div className="p-6">
        <h2 className="text-2xl font-black text-white mb-6 uppercase">Menu</h2>
        <nav className="space-y-2">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                setCurrentPage(item.id as any);
                setLeftPanelOpen(false);
              }}
              className={`w-full text-left px-4 py-3 font-black uppercase border-4 border-black transition-all hover:translate-x-1 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] ${
                currentPage === item.id ? 'bg-[#ffd803]' : 'bg-white'
              }`}
            >
              <FontAwesomeIcon icon={item.icon} className="mr-2" />
              {item.label}
            </button>
          ))}
        </nav>
      </div>
    </div>
  );

  // Right Discord Panel Component
  const RightPanel = () => {
    // Check if member is in voice
    const isInVoice = (memberId: string) => {
      return voiceChannels.some(channel =>
        channel.members.some(m => m.id === memberId)
      );
    };

    return (
      <div
        className={`fixed right-0 top-0 h-full w-80 bg-[#8bd3dd] border-l-4 border-black shadow-[-8px_0px_0px_0px_rgba(0,0,0,1)] z-40 transition-transform duration-300 ease-in-out ${
          rightPanelOpen ? 'translate-x-0' : 'translate-x-full'
        } lg:translate-x-0`}
        style={{ willChange: 'transform' }}
      >
        <div className="p-6 h-full flex flex-col">
          <h2 className="text-2xl font-black mb-4 uppercase">👥 Who's Online</h2>

          <div className="flex-1 space-y-3 overflow-y-auto">
            {/* Loading State */}
            {discordLoading && (
              <div className="bg-white border-4 border-black p-3">
                <p className="font-bold text-center text-sm">
                  Loading Discord data...
                </p>
              </div>
            )}

            {/* Error State */}
            {discordError && !discordLoading && (
              <div className="bg-[#f582ae] border-4 border-black p-3">
                <p className="font-bold text-center text-sm">
                  ⚠️ Connection failed
                </p>
                <p className="text-xs text-center mt-1">
                  {discordError}
                </p>
              </div>
            )}

            {/* Online Members List */}
            {!discordLoading && !discordError && onlineMembers.length === 0 && (
              <div className="bg-white border-4 border-black p-3">
                <p className="font-bold text-center text-sm">
                  No one is online 😴
                </p>
              </div>
            )}

            {!discordLoading && onlineMembers.map((member) => (
              <div
                key={member.id}
                className="bg-[#b8f2e6] border-4 border-black p-3"
              >
                <div className="flex items-center gap-3">
                  {/* Avatar */}
                  <img
                    src={discordApi.getAvatarUrl(member)}
                    alt={member.username}
                    className="w-10 h-10 rounded-full border-2 border-black"
                  />

                  {/* Member Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {/* Status Indicator */}
                      <div className={`w-3 h-3 ${discordApi.getStatusColor(member.status)} rounded-full border-2 border-black`}></div>

                      {/* Username */}
                      <span className="font-black text-sm truncate">
                        {member.displayName || member.username}
                      </span>

                      {/* Voice Indicator */}
                      {isInVoice(member.id) && (
                        <FontAwesomeIcon
                          icon={faMicrophone}
                          className="text-[#9b5de5] text-xs"
                          title="In voice channel"
                        />
                      )}
                    </div>

                    {/* Activity */}
                    {member.activities && member.activities.length > 0 && (
                      <p className="text-xs font-bold mt-1 truncate">
                        {discordApi.formatActivity(member.activities[0])}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Online Count */}
          {!discordLoading && !discordError && onlineMembers.length > 0 && (
            <div className="mt-2 bg-white border-4 border-black p-2 text-center">
              <span className="font-black text-sm">
                {onlineMembers.length} online
              </span>
            </div>
          )}

          {/* Voice Channels Section */}
          {!discordLoading && voiceChannels.length > 0 && (
            <div className="mt-4">
              <h3 className="text-xl font-black mb-2 uppercase flex items-center gap-2">
                <FontAwesomeIcon icon={faMicrophone} className="text-[#9b5de5]" />
                In Voice
              </h3>
              <div className="space-y-2">
                {voiceChannels.map((channel) => (
                  <div
                    key={channel.id}
                    className="bg-[#ffd803] border-4 border-black p-3"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-black text-sm truncate">{channel.name}</span>
                      <span className="bg-[#f582ae] border-2 border-black px-2 py-1 font-black text-xs">
                        {channel.memberCount}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {channel.members.slice(0, 4).map((member) => (
                        <img
                          key={member.id}
                          src={discordApi.getAvatarUrl(member)}
                          alt={member.username}
                          title={member.displayName || member.username}
                          className="w-6 h-6 rounded-full border-2 border-black"
                        />
                      ))}
                      {channel.memberCount > 4 && (
                        <div className="w-6 h-6 rounded-full border-2 border-black bg-black text-white flex items-center justify-center text-xs font-black">
                          +{channel.memberCount - 4}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bot Config Button */}
          <button
            onClick={() => setCurrentPage('bot-config')}
            className="mt-4 w-full bg-[#9b5de5] border-4 border-black p-4 font-black uppercase transition-all hover:translate-y-1 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"
          >
            🤖 Bot Config
          </button>
        </div>
      </div>
    );
  };

  // Mobile toggle buttons
  const MobileButtons = () => (
    <>
      {/* Burger Menu Button - hide when left panel is open */}
      {!leftPanelOpen && (
        <button
          onClick={() => setLeftPanelOpen(true)}
          className="lg:hidden fixed top-4 left-4 z-50 bg-[#001858] border-4 border-black p-3 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
        >
          <div className="w-6 h-5 flex flex-col justify-between">
            <div className="w-full h-1 bg-white"></div>
            <div className="w-full h-1 bg-white"></div>
            <div className="w-full h-1 bg-white"></div>
          </div>
        </button>
      )}

      {/* Discord Button - hide when right panel is open */}
      {!rightPanelOpen && (
        <button
          onClick={() => setRightPanelOpen(true)}
          className="lg:hidden fixed top-4 right-4 z-50 bg-[#5865F2] border-4 border-black p-3 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
        >
          <svg className="w-6 h-6 fill-white" viewBox="0 0 127.14 96.36">
            <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z"/>
          </svg>
        </button>
      )}
    </>
  );

  // Overlay for mobile when panels are open
  const Overlay = () => (
    <>
      {(leftPanelOpen || rightPanelOpen) && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30 transition-opacity duration-300"
          onClick={() => {
            setLeftPanelOpen(false);
            setRightPanelOpen(false);
          }}
        />
      )}
    </>
  );

  // Bot Config Page
  if (currentPage === 'bot-config') {
    return (
      <>
        <LeftPanel />
        <RightPanel />
        <MobileButtons />
        <Overlay />
        <BotConfig onBack={() => setCurrentPage('home')} />
      </>
    );
  }

  // Activities Page
  if (currentPage === 'activities') {
    return (
      <>
        <LeftPanel />
        <RightPanel />
        <MobileButtons />
        <Overlay />
        <div className="min-h-screen p-8 lg:ml-64 lg:mr-80">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-black mb-8 text-center">
              ACTIVITIES
            </h1>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activities.map((activity) => (
                <button
                  key={activity.id}
                  onClick={() => {
                    if (activity.name === 'Tournament') {
                      setCurrentPage('tournament' as any);
                    } else if (activity.name === 'BotC') {
                      setCurrentPage('botc' as any);
                    } else if (activity.name === 'Drinking Games') {
                      setCurrentPage('drinking-games' as any);
                    }
                  }}
                  disabled={activity.name !== 'Tournament' && activity.name !== 'BotC' && activity.name !== 'Drinking Games'}
                  className={`${activity.color} border-4 border-black p-8 text-2xl font-black uppercase transition-all ${
                    (activity.name === 'Tournament' || activity.name === 'BotC' || activity.name === 'Drinking Games')
                      ? 'hover:translate-x-1 hover:translate-y-1 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-x-2 active:translate-y-2 cursor-pointer'
                      : 'opacity-30 cursor-not-allowed'
                  }`}
                >
                  {activity.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </>
    );
  }

  // Tournament Page
  if (currentPage === 'tournament') {
    return (
      <>
        <LeftPanel />
        <RightPanel />
        <MobileButtons />
        <Overlay />
        <Tournament onBack={() => setCurrentPage('activities')} />
      </>
    );
  }

  // BotC Page
  if (currentPage === 'botc') {
    return (
      <>
        <LeftPanel />
        <RightPanel />
        <MobileButtons />
        <Overlay />
        <BotC onBack={() => setCurrentPage('activities')} />
      </>
    );
  }

  // Drinking Games Page
  if (currentPage === 'drinking-games') {
    return (
      <>
        <LeftPanel />
        <RightPanel />
        <MobileButtons />
        <Overlay />
        <DrinkingGames onBack={() => setCurrentPage('activities')} />
      </>
    );
  }

  // Music Page
  if (currentPage === 'music') {
    return (
      <>
        <LeftPanel />
        <RightPanel />
        <MobileButtons />
        <Overlay />
        <Music onBack={() => setCurrentPage('home')} />
      </>
    );
  }

  // Game Tracker Page
  if (currentPage === 'game-tracker') {
    return (
      <>
        <LeftPanel />
        <RightPanel />
        <MobileButtons />
        <Overlay />
        <GameTracker />
      </>
    );
  }

  // Leaderboards Page
  if (currentPage === 'leaderboards') {
    return (
      <>
        <LeftPanel />
        <RightPanel />
        <MobileButtons />
        <Overlay />
        <Leaderboards />
      </>
    );
  }

  // AI Chat Page
  if (currentPage === 'ai-chat') {
    return (
      <>
        <LeftPanel />
        <RightPanel />
        <MobileButtons />
        <Overlay />
        <AIChat />
      </>
    );
  }

  // Placeholder pages
  if (currentPage !== 'home') {
    return (
      <>
        <LeftPanel />
        <RightPanel />
        <MobileButtons />
        <Overlay />
        <div className="min-h-screen p-8 lg:ml-64 lg:mr-80">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-black mb-8 text-center uppercase">
              {currentPage}
            </h1>
            <div className="bg-white border-4 border-black p-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
              <p className="text-xl font-bold text-center">
                Coming soon...
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Home Page
  return (
    <>
      <LeftPanel />
      <RightPanel />
      <MobileButtons />
      <Overlay />
      <div className="min-h-screen p-8 lg:ml-64 lg:mr-80">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-12 text-center">
            <h1 className="text-6xl md:text-8xl font-black mb-4 tracking-tight">
              SHIT FUCKS
            </h1>
            <p className="text-xl md:text-2xl font-bold border-4 border-black bg-white inline-block px-6 py-3 rotate-1 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
              Your squad HQ 🎯
            </p>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Birthdays Section */}
            <div className="md:col-span-2 bg-[#ffd803] border-4 border-black p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
              <h2 className="text-3xl font-black mb-4 uppercase">🎂 Upcoming Birthdays</h2>
              <div className="space-y-3">
                {upcomingBirthdays.map((birthday, index) => (
                  <div
                    key={index}
                    className={`border-4 border-black p-4 ${
                      index === 0 ? 'bg-[#f582ae]' : 'bg-white'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-black">{birthday.name}</span>
                      <div className="text-right">
                        <div className="text-lg font-black">
                          {formatDate(birthday.month, birthday.day)}
                        </div>
                        <div className="text-sm font-bold">
                          {birthday.daysUntil === 0
                            ? 'TODAY! 🎉'
                            : `in ${birthday.daysUntil} day${birthday.daysUntil !== 1 ? 's' : ''}`}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Links Section */}
            <div className="space-y-4">
              {/* WhatsApp Button */}
              <button className="w-full bg-[#25D366] border-4 border-black p-6 font-black uppercase transition-all hover:translate-x-1 hover:translate-y-1 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-x-2 active:translate-y-2 text-white">
                <FontAwesomeIcon icon={faWhatsapp} className="text-3xl mb-2" />
                <div className="mt-2">WhatsApp</div>
              </button>

              {/* Discord Button */}
              <a
                href="https://discord.gg/FafRdS8r"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#5865F2] border-4 border-black p-6 font-black uppercase transition-all hover:translate-x-1 hover:translate-y-1 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-x-2 active:translate-y-2 text-white text-center"
              >
                <FontAwesomeIcon icon={faDiscord} className="text-3xl mb-2" />
                <div className="mt-2">Discord</div>
              </a>

              {/* Locations Button */}
              <button className="w-full bg-[#f582ae] border-4 border-black p-6 font-black uppercase transition-all hover:translate-x-1 hover:translate-y-1 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-x-2 active:translate-y-2">
                <FontAwesomeIcon icon={faLocationDot} className="text-3xl mb-2" />
                <div className="mt-2">Locations</div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default App;
